// ============================================================================
// By account manager — PURE SURFACE (same shape as the other tabs: the shell
// owns the client and the state).
//
// ⛔ KEEPING SUBS IS AN ACCOUNT MANAGER OUTCOME, so the roster is grouped by
// whoever holds most of each sub's buildings. That is what `lead_am_eid` on the
// tier view means — the manager of the market where the sub does most of their
// work, not an ownership field somebody types.
//
// ⛔ THE SUBS WITH NO ACCOUNT MANAGER GET A ROW OF THEIR OWN, AT THE BOTTOM AND
// NAMED. A sub with no building has no lead manager to derive, and those are
// precisely the highest-risk records on the platform — anyone who has just lost
// their last building is in that row. Dropping them because they have no owner
// is the same defect the tier view had until 2026-09-11.
//
// ⛔ Bench cover is shown BESIDE the roster counts on purpose. An account manager
// with eleven subs and nobody ready behind them in their markets cannot replace
// one without scrambling, and that is invisible if the two tables live apart.
// ============================================================================
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { TierRow } from './tier-roster-parts';
import { TENURE_FOOTNOTE } from './tier-roster-parts';
import type { BenchRow } from './hooks/use-sub-bench';
import { coversMarket } from './bench-parts';

export const NO_MANAGER = '__none__';

export interface AmRow {
  eid: string;
  name: string;
  markets: string[];
  subs: number;
  buildings: number;
  t1: number;
  t2: number;
  t3: number;
  t4: number;
  lost60: number;
  anyBulk: boolean;
  /** Subs with no building at all — nothing to lose next, and easy to miss. */
  noBuilding: number;
  /** Ready subs on the bench covering any market this manager works. */
  benchCover: number;
}

export type AmSortKey = keyof Pick<
  AmRow,
  'name' | 'subs' | 'buildings' | 't1' | 't2' | 't3' | 't4' | 'lost60' | 'benchCover'
>;

export function byAccountManager(rows: TierRow[], bench: BenchRow[]): AmRow[] {
  const groups = new Map<string, TierRow[]>();
  for (const r of rows) {
    const key = r.lead_am_eid ?? NO_MANAGER;
    const held = groups.get(key);
    if (held) held.push(r);
    else groups.set(key, [r]);
  }

  const out: AmRow[] = [];
  for (const [eid, subs] of groups) {
    const markets = Array.from(
      new Set(subs.map((r) => r.lead_market).filter((m): m is string => !!m)),
    ).sort();
    out.push({
      eid,
      name:
        eid === NO_MANAGER
          ? 'No account manager'
          : (subs.find((r) => r.lead_am_name)?.lead_am_name ?? eid),
      markets,
      subs: subs.length,
      buildings: subs.reduce((a, r) => a + r.active_facility_count, 0),
      t1: subs.filter((r) => r.tier === 1).length,
      t2: subs.filter((r) => r.tier === 2).length,
      t3: subs.filter((r) => r.tier === 3).length,
      t4: subs.filter((r) => r.tier === 4).length,
      lost60: subs.reduce((a, r) => a + r.facilities_lost_60d, 0),
      anyBulk: subs.some((r) => r.bulk_handover_60d),
      noBuilding: subs.filter((r) => r.active_facility_count === 0).length,
      benchCover: markets.length
        ? bench.filter((b) => markets.some((m) => coversMarket(b, m))).length
        : 0,
    });
  }

  return out;
}

export function sortAm(rows: AmRow[], key: AmSortKey, desc: boolean): AmRow[] {
  // ⛔ The unassigned bucket is pinned last by being sorted SEPARATELY, not by a
  // rule inside the comparator. A comparator that returns "always last" is
  // undone the moment the caller reverses the array for a descending sort —
  // which is exactly what the first version of this did, and what its own test
  // caught: "most subs" put the bucket on top and buried the people.
  const people = rows.filter((r) => r.eid !== NO_MANAGER);
  const bucket = rows.filter((r) => r.eid === NO_MANAGER);
  people.sort((a, b) =>
    key === 'name' ? a.name.localeCompare(b.name) : (a[key] as number) - (b[key] as number),
  );
  if (desc) people.reverse();
  return [...people, ...bucket];
}

const COLUMNS: Array<{ key: AmSortKey; label: string; num?: boolean; title?: string }> = [
  { key: 'name', label: 'Account manager' },
  { key: 'subs', label: 'Subs', num: true },
  { key: 'buildings', label: 'Buildings', num: true },
  { key: 't1', label: 'T1', num: true, title: 'Tier 1 — conferred by hand' },
  { key: 't2', label: 'T2', num: true, title: 'Tier 2 — 90+ days, three or more buildings' },
  { key: 't3', label: 'T3', num: true, title: 'Tier 3 — 90+ days, under three buildings' },
  { key: 't4', label: 'T4', num: true, title: 'Tier 4 — under 90 days with TCS' },
  { key: 'lost60', label: 'Lost · 60d', num: true, title: 'Buildings lost in the last 60 days' },
  { key: 'benchCover', label: 'Bench', num: true, title: 'Ready subs covering their markets' },
];

export function AccountManagerView({
  rows,
  unassigned,
  sortKey,
  sortDesc,
  onSort,
  renderMarkets,
}: {
  /** Managers only. ⛔ The subs with no manager are NOT rows here — Ben,
   *  2026-09-14 — but their count is stated under the table, because a table
   *  that silently covers 30 of 65 subcontractors is a table that lies by
   *  omission. */
  rows: AmRow[];
  unassigned: AmRow | null;
  sortKey: AmSortKey;
  sortDesc: boolean;
  onSort: (key: AmSortKey) => void;
  renderMarkets: (row: AmRow) => ReactNode;
}) {
  const sorted = sortAm(rows, sortKey, sortDesc);
  const exposed = sorted.filter((r) => r.eid !== NO_MANAGER && r.subs > 0 && r.benchCover === 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-tcs-text-secondary">
        Keeping subcontractors is an account manager outcome, so the roster is grouped by whoever
        holds most of each sub&rsquo;s buildings. Click a column to sort by it.
      </p>

      {exposed.length > 0 && (
        <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3 text-sm">
          <span className="font-semibold text-st-danger-fg">
            {exposed.length} manager{exposed.length === 1 ? '' : 's'} with nobody ready behind them:
          </span>{' '}
          <span className="text-tcs-text-secondary">
            {exposed.map((r) => r.name).join(', ')}. Losing a sub in their market means scrambling.
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-tcs-text-muted">
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  title={c.title}
                  className={`py-1 pr-3 font-medium ${c.num ? 'text-right' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => onSort(c.key)}
                    className="inline-flex items-center gap-0.5 uppercase hover:text-tcs-text"
                  >
                    {c.label}
                    {sortKey === c.key &&
                      (sortDesc ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronUp className="h-3 w-3" />
                      ))}
                  </button>
                </th>
              ))}
              <th className="py-1 font-medium">Markets</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr
                key={r.eid}
                className={`border-t border-tcs-border align-top ${
                  r.eid === NO_MANAGER ? 'bg-tcs-surface' : ''
                }`}
              >
                <td className="py-1.5 pr-3 font-medium text-tcs-text">
                  {r.name}
                  {r.noBuilding > 0 && (
                    <div className="text-[11px] font-normal text-st-danger-fg">
                      {r.noBuilding} with no building at all
                    </div>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.subs}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.buildings}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-tcs-text-secondary">
                  {r.t1 || '—'}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-tcs-text-secondary">
                  {r.t2 || '—'}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-tcs-text-secondary">
                  {r.t3 || '—'}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {r.t4 ? <span className="font-medium text-st-warn-fg">{r.t4}</span> : '—'}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {r.lost60 ? (
                    <span className="font-medium text-st-danger-fg">
                      {r.lost60}
                      {r.anyBulk && <span title="Includes a whole book moved at once">*</span>}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {r.benchCover === 0 && r.subs > 0 && r.eid !== NO_MANAGER ? (
                    <span className="font-medium text-st-danger-fg">0</span>
                  ) : (
                    r.benchCover || '—'
                  )}
                </td>
                <td className="py-1.5 text-xs text-tcs-text-secondary">{renderMarkets(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-tcs-text-muted">
        {unassigned && unassigned.subs > 0 && (
          <>
            <span className="font-medium text-tcs-text-secondary">
              {unassigned.subs} subcontractors are not shown.
            </span>{' '}
            They hold no building, so there is no manager to derive one from — subs whose assignments ended,
            one starting next month, and dormant records nobody has called.{' '}
          </>
        )}
        <span className="font-medium">*</span> includes a whole book moved to one incoming sub at
        once, which is a handover rather than a sub losing work one building at a time.{' '}
        {TENURE_FOOTNOTE}
      </p>
    </div>
  );
}
